import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Users, Banknote, Megaphone, ShieldCheck, Bell, Settings, LogOut, LayoutDashboard, ClipboardCheck, Mail, TrendingUp, type LucideIcon, Database, PhoneCall } from 'lucide-react';
import { useDbWake } from '../hooks/useDbWake';
import { DbWakeBanner } from '../components/DbWakeBanner';
import { useAuth } from '../auth/AuthProvider';
import type { CrmRole } from '../auth/AuthProvider';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { GlobalSearchBar } from '../components/GlobalSearchBar';
import { DemoSwitcher } from '../components/DemoSwitcher';

// ---- Navigation item definition ----

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  allowedRoles: CrmRole[];
  badgeKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard',  label: 'Dashboard',  icon: LayoutDashboard, allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/members',      label: 'Members',       icon: Users,          allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/cross-sell',   label: 'Cross-Sell',    icon: TrendingUp,     allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/core-lookup',  label: 'Core Lookup',   icon: Database,       allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/review-queue', label: 'Review Queue',  icon: ClipboardCheck, allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/templates',    label: 'Templates',     icon: Mail,           allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/loans',        label: 'Loans',         icon: Banknote,       allowedRoles: ['loan_officer', 'compliance_officer', 'admin'] },
  { path: '/collections',  label: 'Collections',   icon: PhoneCall,      allowedRoles: ['loan_officer', 'compliance_officer', 'admin'] },
  { path: '/campaigns',  label: 'Campaigns',  icon: Megaphone,   allowedRoles: ['marketing_staff', 'admin'] },
  { path: '/compliance', label: 'Compliance', icon: ShieldCheck, allowedRoles: ['compliance_officer', 'admin'] },
  { path: '/alerts',     label: 'Alerts',     icon: Bell,        allowedRoles: ['loan_officer', 'marketing_staff', 'compliance_officer', 'admin'] },
  { path: '/settings',   label: 'Settings',   icon: Settings,    allowedRoles: ['admin'] },
];

// ---- NavIcon component ----

function NavIcon({ Icon }: { Icon: LucideIcon }): React.ReactElement {
  return <Icon className="h-5 w-5 flex-shrink-0" aria-hidden />;
}

// ---- AppLayout ----

/**
 * AppLayout — main application shell with sidebar navigation and content area.
 *
 * Structure:
 * - Fixed sidebar: CU name header, role-filtered nav items, user info footer
 * - Main content area: breadcrumb header + <Outlet /> for child routes
 *
 * Nav items are filtered by the authenticated user's roles.
 * CU 2.0 shell: navy sidebar, active item = white/10 wash with an orange icon (no solid fill, no left bar).
 */
export function AppLayout(): React.ReactElement {
  const { user, hasAnyRole } = useAuth();
  const navigate = useNavigate();

  const visibleNavItems = NAV_ITEMS.filter((item) => hasAnyRole(item.allowedRoles));

  function handleSignOut(): void {
    // Clear dev token if present
    localStorage.removeItem('crm_dev_token');
    // In production: call /api/auth/logout to clear HttpOnly cookie
    navigate('/login', { replace: true });
  }

  // A signed-in session (8 h token) skips the login page, so the shell wakes the database too. When this
  // page had to wait, reload once it is ready so every view refetches what failed while it was down.
  const wake = useDbWake();
  useEffect(() => {
    if (wake.state === 'ready' && wake.waited) window.location.reload();
  }, [wake.state, wake.waited]);

  const cuName = user?.cuName ?? 'Credit Union';
  const userEmail = user?.email ?? '';
  const primaryRole = user?.roles[0] ?? '';

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* ---- Sidebar ---- */}
      <aside
        className="flex flex-col w-64 flex-shrink-0 bg-navy"
        aria-label="Main navigation"
      >
        {/* Brand block: CU 2.0 chevron + product name, then the signed-in credit union */}
        <div className="flex items-center gap-2.5 px-5 h-[72px] border-b border-navy-light">
          <img src="/cu2-icon.svg" alt="" width={28} height={28} className="flex-shrink-0" />
          <div className="min-w-0 leading-tight">
            <p className="font-heading text-sm font-extrabold tracking-[-0.01em] text-white">CRM</p>
            <p className="mt-0.5 text-xs text-white/60 truncate">{cuName}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Sidebar">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 h-10 px-3 rounded-md text-sm transition-colors duration-[120ms]',
                  isActive ? 'nav-link-active' : 'nav-link-idle',
                ].join(' ')
              }
            >
              <NavIcon Icon={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Footer */}
        <div className="px-4 py-4 border-t border-navy-light">
          <div className="flex items-center gap-3 min-w-0 mb-3">
            {/* Avatar */}
            <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 font-heading text-xs font-bold text-on-brand bg-brand">
              {userEmail.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium truncate">{userEmail}</p>
              <p className="text-xs text-white/60 capitalize">
                {primaryRole.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-[120ms] text-white/70 hover:text-white hover:bg-navy-light"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </button>
        </div>
      </aside>

      {/* ---- Main content ---- */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top header bar */}
        <header className="flex items-center justify-between px-6 h-[72px] bg-surface border-b border-line shadow-xs">
          <div>
            {/* Breadcrumb placeholder — individual pages can override via a portal */}
            <p className="font-heading text-lg font-bold text-navy">CU 2.0 CRM</p>
          </div>
          <div className="flex items-center gap-3">
            <GlobalSearchBar />
            <DemoSwitcher />
            <NotificationBell />
            <span className="text-sm text-ink-muted">{userEmail}</span>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-on-brand font-heading text-xs font-bold bg-brand">
              {userEmail.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {wake.state !== 'ready' && wake.state !== 'checking' && (
            <div className="mb-4">
              <DbWakeBanner wake={wake} />
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
